import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Avatar,
    IconButton,
    Checkbox,
    Chip,
    Tooltip,
    LinearProgress,
} from '@mui/material';
import {
    Check as CheckIcon,
    CheckCircleOutline,
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
    HighlightOff,
    SubdirectoryArrowRight,
    CallSplit as CallSplitIcon,
} from '@mui/icons-material';
import { format, parseISO, isValid } from 'date-fns';
import { getDateLocale } from '../lib/dateLocale';

/**
 * Safely formats a date string. Returns empty string if invalid.
 */
function safeFormat(
    dateStr: string | null | undefined,
    formatStr: string,
): string {
    if (!dateStr) return '';
    const parsed = parseISO(dateStr);
    return isValid(parsed)
        ? format(parsed, formatStr, { locale: getDateLocale() })
        : '';
}
import * as PropTypes from 'prop-types';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { useTheme } from '@mui/material/styles';
import {
    updateElementInArray,
    deleteElementFromArray,
    commaSeparated,
    formatPriceByLocale,
} from '../lib/utils';
import {
    AppContext,
    // SttCell,
    SttChipPForm,
    SttChipModule,
    SttLabelPeriod,
    SttLabelDuration,
    SttMenu,
    SttLabelWeekdays,
    SttNoResults,
} from './all';
import React from 'react';

type Column = {
    name?: string;
    title?: string;
    field?: string;
    type?: string;
    value?: (row: any) => any;
    checkOld?: boolean;
    to?: (row: any) => string;
    params?: (row: any) => any;
    color?: string;
    onClick?: (row: any) => void;
    className?: string;
    align?: 'left' | 'right' | 'center';
    style?: React.CSSProperties;
    width?: number;
};

type SelectionAction = {
    caption?: string;
    icon?: React.ReactNode;
    onClick?: (rows: any[]) => void;
};

type Selection = {
    actions?: SelectionAction[];
};

type Total = {
    value?: number;
    index: number;
    adornment?: string;
};

type Props = {
    autoload?: boolean;
    columns?: Column[];
    data?: any;
    onFetch?: (params?: any) => void;
    onChangePagination?: ((page: number) => void) | null;
    loading?: boolean;
    loadingValue?: boolean;
    onClickRow?: (row: any) => void;
    linkTo?: (row: any) => string;
    childrenEnabled?: boolean;
    totals?: Total[];
    hidePagination?: boolean;
    selection?: Selection;
    selectRows?: any[];
    setSelectRows?: (rows: any[]) => void;
    forceUpdateSwitch?: any;
    localPagination?: boolean;
    perPage?: number;
};

/*
TODO: Este componente se repinta innecesariamente varias veces por ciclo. Eso debe corregirse en algún momento.
Problema:
1. Si cambia el prop 'data' el componente se repinta.
2. Al cambiar 'data', cambia el state 'internalData' (usado para la lógica de visibilidad de los hijos), así que se repinta de nuevo
3. Al cambiar el state 'page' el componente se repinta.
*/

// TODO: Este componente debería aceptar un handler externo para la paginación (para poder llevar cuenta desde fuera y, por ejemplo, poder recargar los datos manteniendo la página actual)

/*
 * Note: for some internal state updates, SttTable now uses the internalData value which is just copied from the data prop.
 * */
function SttTable({
    autoload = true,
    columns,
    data,
    onFetch,
    onChangePagination = null,
    loading = false,
    loadingValue,
    onClickRow,
    linkTo,
    childrenEnabled = false,
    totals,
    hidePagination = false,
    selection,
    selectRows,
    setSelectRows,
    localPagination = false,
    perPage = 20,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [internalData, setInternalData] = useState<any>(data);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(perPage);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [selectedAllRows, setSelectedAllRows] = useState<boolean>(false);
    const [loadingData, setLoadingData] = useState<boolean>(false);

    const hasIdColumn = columns?.[0] && columns[0].title === '#';
    // Init. Run autoload onFetch if necessary
    useEffect(() => {
        if (autoload && onFetch) {
            if (localPagination) {
                onFetch({ page: 1, rows: 1000000 });
            } else {
                onFetch();
            }
        }
    }, []);

    useEffect(() => {
        if (loadingValue === true || loadingValue === false) {
            setLoadingData(loadingValue);
        }
    }, [loadingValue]);

    /**
     * Input data changes (count,rows)
     */
    useEffect(() => {
        if (data?.rows) {
            if (loading) {
                setLoadingData(false);
            }

            setChildVisibility(data.rows, null, false);
            setInternalData(data);

            // """auto-detect""" rows per page if it wasn't specified
            if (rowsPerPage === 0) {
                setRowsPerPage(data.rows.length);
            }
        }
    }, [data]);

    /**
     * Select rows data changes.
     */
    useEffect(() => {
        if (selectRows) setSelectedRows(selectRows);
    }, [selectRows]);

    useEffect(() => {
        if (setSelectRows) setSelectRows(selectedRows);
    }, [selectedRows]);

    // Set child visibility on all data rows
    function setVisibility(row: any, visibility: boolean) {
        const { rows } = internalData;

        setChildVisibility(rows, row, visibility);

        setInternalData({ ...internalData, rows });
    }

    // Recursively set child visibility for a certain row
    function setChildVisibility(rows: any[], row: any, visibility: boolean) {
        if (!childrenEnabled) {
            return;
        }

        const toggleAll = !row;

        rows.forEach((item: any) => {
            // Ignore childless rows
            if (!item?.children?.rows || item.children.rows.count === 0) {
                return;
            }
            item.childrenVisible =
                toggleAll || item === row
                    ? visibility
                    : item.childrenVisible || false;

            if (item.children) {
                setChildVisibility(item.children.rows, row, visibility);
            }
        });
    }

    // Specific column widths depending on type
    function getColWidth(col: Column): number | string {
        let w: number | string;
        switch (col.type) {
            case 'id':
                w = 90;
                break;
            case 'dmy':
                w = 110;
                break;
            case 'dmyhm':
                w = 130;
                break;
            case 'payment.price':
                w = 90;
                break;
            case 'price':
                w = 100;
                break;
            case 'period':
                w = 200;
                break;
            case 'periodPlain':
                w = 215;
                break;
            case 'periodTime':
                w = 130;
                break;
            case 'active':
                w = 20;
                break;
            case 'pf':
                w = 100;
                break;
            case 'counter':
                w = 50;
                break;
            case 'avatar':
                w = 45;
                break;
            case 'user.login':
                w = 110;
                break;
            case 'module':
                w = 100;
                break;
            case 'enrollment':
                w = 180;
                break;
            case 'tooltip':
                w = 20;
                break;
            default:
                w = '';
        }
        return col.width || w;
    }

    /**
     * Render column header
     */
    const renderHeader = (col: Column, idx: number) => {
        let align: 'left' | 'right' | 'center' = 'center';
        const w = getColWidth(col);

        if (col.align) {
            align = col.align;
        }

        // TODO: Improve width handling because right now the table flickers like crazy when traversing its pages
        return (
            <TableCell
                key={idx}
                align={align}
                style={col.style || { minWidth: w }}
            >
                <b>{col.title}</b>
            </TableCell>
        );
    };

    // Render a cell, mostly depending on col.type
    const renderCell = (row: any, col: Column, idx: number, depth = 0) => {
        let val: any;
        const cls: string[] = [];
        let content: any;
        let align: 'left' | 'right' | 'center' = 'center';
        let old: boolean | undefined;

        // Set default value for "val"
        if (col.value && col.value instanceof Function) {
            val = col.value(row);
        } else {
            val = row[col.field!];
        }

        switch (col.type) {
            // Id
            case 'id':
                align = 'center';
                break;

            // DMY
            case 'dmy':
                if (val) {
                    if (col.checkOld) {
                        if (new Date(val) < new Date()) {
                            old = true;
                        }
                    }
                    align = 'center';
                    content = (
                        <div style={old ? { color: 'var(--destructive)' } : {}}>
                            {safeFormat(val, 'd MMM yyyy')}
                        </div>
                    );
                }
                break;

            // DMY HM
            case 'dmyhm':
                if (val) {
                    if (col.checkOld) {
                        if (new Date(val) < new Date()) {
                            old = true;
                        }
                    }
                    align = 'center';
                    content = (
                        <div style={old ? { color: 'var(--destructive)' } : {}}>
                            {safeFormat(val, 'd MMM yyyy, H:mm')}
                        </div>
                    );
                }
                break;

            // D MMM YYYY - D MMM YYYY
            case 'dmyRange':
                align = 'center';
                content = (
                    <div>{`${safeFormat(val?.ini, 'd MMM yyyy')} - ${safeFormat(val?.end, 'd MMM yyyy')}`}</div>
                );
                break;

            // HH:MM
            case 'hm':
                align = 'center';
                content = <div>{safeFormat(val, 'HH:mm')}</div>;
                break;

            // HH:MM - HH:MM
            case 'hmRange':
                align = 'center';
                content = (
                    <div>{`${safeFormat(val?.ini, 'HH:mm')} - ${safeFormat(val?.end, 'HH:mm')}`}</div>
                );
                break;

            // Price. Acepta o bien un valor plano o bien una tupla {paid, price} con la que se determina si debe pintarse en rojo o en negro
            case 'price':
                align = 'right';
                if (typeof val !== 'object') {
                    content = (
                        <div>
                            {val !== null && val !== undefined ? `${val}` : ''}
                        </div>
                    );
                } else if (val && val.paid !== undefined) {
                    content = (
                        <div
                            style={
                                val.paid ? {} : { color: 'var(--destructive)' }
                            }
                        >
                            {val?.price
                                ? `${formatPriceByLocale(
                                      val.price,
                                      val.priceShape?.currency ?? undefined,
                                      val.priceShape?.locale,
                                  )}`
                                : ''}
                        </div>
                    );
                }
                break;

            // debt: Always red
            case 'debt':
                align = 'right';
                content = (
                    <div style={{ color: 'var(--destructive)' }}>
                        {typeof val === 'number'
                            ? `${formatPriceByLocale(
                                  val,
                                  cxt?.sc?.city?.country?.currency,
                              )}`
                            : ''}
                    </div>
                );
                break;

            // Rate [duration, price]
            case 'rate':
                align = 'center';
                content = val.duration ? (
                    <span>
                        <b>{`${val.duration}m`}</b> /{' '}
                        <span>{`${formatPriceByLocale(
                            val.price,
                            cxt?.sc?.city?.country?.currency,
                        )}`}</span>
                    </span>
                ) : (
                    <span />
                );
                break;

            // Period that does care about the current date
            case 'period':
                content = <SttLabelPeriod ini={val.ini} end={val.end} />;
                break;

            // Period with weekdays below
            case 'periodWithWeekdays':
                content = (
                    <Box>
                        <SttLabelPeriod ini={val.ini} end={val.end} />
                        {val.weekdays && (
                            <Box mt={0.5}>
                                <SttLabelWeekdays weekdays={val.weekdays} />
                            </Box>
                        )}
                    </Box>
                );
                break;

            // Period that doesn't care about the current date
            case 'periodPlain':
                if (val?.ini !== val?.end) {
                    content = `${safeFormat(val?.ini, 'd MMM yyyy')} - ${safeFormat(val?.end, 'd MMM yyyy')}`;
                } else if (val?.ini) {
                    content = safeFormat(val.ini, 'd MMM yyyy');
                } else {
                    content = <span />;
                }
                break;

            // Period of time: H:mm - H:mm, doesn't care about current date
            case 'periodTime':
                if (val?.ini !== val?.end) {
                    content = `${safeFormat(val?.ini, 'H:mm')} - ${safeFormat(val?.end, 'H:mm')}`;
                } else if (val?.ini) {
                    content = safeFormat(val.ini, 'H:mm');
                } else {
                    content = <span />;
                }
                break;

            // Link
            // Example:
            //         to: row => (`/sc/group/${row.id}`),
            //        params: row => ({trash: row.trash})
            case 'link':
                if (col.to) {
                    let params: any = {};
                    if (col.params) {
                        params = col.params(row);
                    }

                    content = (
                        <Link
                            style={{
                                color: col.color || 'var(--brand-text)',
                                textDecoration: 'none',
                                fontWeight: 'bold',
                            }}
                            to={{
                                pathname: col.to(row),
                                // Tenemos que usar a la fuerza el campo .search que provee react-router-dom
                                // Los parámetros que aquí se especifiquen pueden luego capturarse con el hook useQuery
                                search: Object.keys(params).reduce(
                                    (result, key, i) =>
                                        // Esto covierte {param1: valor1, param2: valor2, ...} en "?param1=valor1&param2=valor2..."
                                        `${
                                            result + (i > 0 ? '&' : '?') + key
                                        }=${params[key]}`,
                                    '',
                                ),
                            }}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.textDecoration =
                                    'underline')
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.textDecoration = 'none')
                            }
                        >
                            {val}
                        </Link>
                    );
                }
                if (col.onClick) {
                    content = (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                col.onClick!(row);
                            }}
                            style={{
                                color: 'var(--brand-text)',
                                textDecoration: 'none',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                            }}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.textDecoration =
                                    'underline')
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.textDecoration = 'none')
                            }
                        >
                            {val}
                        </a>
                    );
                }
                align = 'left';
                break;

            // User login
            case 'user.login':
                content = val ? `@${val.login}` : '';
                align = 'center';
                break;

            // Active
            case 'active':
                if (val) {
                    content = <CheckIcon />;
                } else {
                    content = <span />;
                }

                align = 'center';
                break;

            // Payment method
            case 'pf':
                content = val ? <SttChipPForm pf={val} /> : '';
                align = 'center';
                break;

            // Counter (just a number)
            case 'counter':
                align = 'center';
                if (val === 0) val = '';
                break;

            // Avatar
            case 'avatar':
                align = 'center';
                content = (
                    <div>
                        <Avatar src={val} />
                    </div>
                );
                break;

            // Module
            case 'module':
                content = <SttChipModule module={val} />;
                align = 'center';
                break;

            // Enrollment: enrollment fee, periodic fee / interval
            case 'enrollment':
                const { enrollment, fee, interval } = val;

                if (enrollment) {
                    if (fee && interval) {
                        content = `${formatPriceByLocale(
                            enrollment,
                            cxt?.sc?.city?.country?.currency,
                        )}, ${formatPriceByLocale(
                            fee,
                            cxt?.sc?.city?.country?.currency,
                        )} / ${t(interval)}`;
                    } else {
                        content = `${formatPriceByLocale(
                            enrollment,
                            cxt?.sc?.city?.country?.currency,
                        )}`;
                    }
                } else if (fee && interval) {
                    content = `${formatPriceByLocale(
                        fee,
                        cxt?.sc?.city?.country?.currency,
                    )} / ${t(interval)}`;
                } else {
                    content = <span />; // Again, to avoid the if(!content) below
                }
                break;

            case 'nonZero':
                content = val > 0 ? val : <span />;
                break;

            // Array of days of the week [0 = Sunday, 1 = Monday, ...]
            case 'weekdays':
                content = <SttLabelWeekdays weekdays={val} />;
                break;

            // Duration
            case 'duration':
                content = <SttLabelDuration duration={val} />;
                break;

            // Binary status: yes/no, ok/error etc. If the field value evaluates to true, renders a green 'check' icon. Red 'cross' icon otherwise.
            case 'binaryStatus':
                content = val ? (
                    <CheckCircleOutline style={{ color: 'var(--ok-ink)' }} />
                ) : (
                    <HighlightOff style={{ color: '#df3525' }} />
                );
                break;

            case 'facility':
                content = val ? (
                    <Chip
                        size="small"
                        label={row.facility.name}
                        sx={{ cursor: 'pointer' }}
                    />
                ) : (
                    <span />
                );
                break;

            case 'user':
                content = val ? <Chip size="small" label={val} /> : <span />;
                break;
            case 'text':
                content = val || <span />;
                break;

            case 'tooltip':
                content = (
                    <Tooltip
                        arrow
                        placement="top"
                        title={val.value}
                        style={{
                            cursor: 'pointer',
                            marginLeft: 4,
                            marginRight: 4,
                            marginTop: 2,
                            marginBottom: 2,
                        }}
                    >
                        <span onClick={() => col.onClick!(row)}>
                            {val.label}
                        </span>
                    </Tooltip>
                );
                break;
        }

        if (!content) {
            content = val;
        }

        // Custom classes
        if (col.className) {
            cls.push(col.className);
        }

        // Column align
        if (col.align) {
            align = col.align;
        }

        // If linkTo is provided and this is the first text column (after id column if present), wrap content in a Link
        const isLinkColumn =
            linkTo && idx === (hasIdColumn ? 1 : 0) && col.type !== 'id';
        let wrappedContent = content;

        if (isLinkColumn && content) {
            align = 'left';
            wrappedContent = (
                <Link
                    to={linkTo(row)}
                    onClick={(e: any) => e.stopPropagation()}
                    style={{
                        color: 'var(--brand-text)',
                        textDecoration: 'none',
                        fontWeight: 'bold',
                    }}
                    onMouseEnter={(e) =>
                        (e.currentTarget.style.textDecoration = 'underline')
                    }
                    onMouseLeave={(e) =>
                        (e.currentTarget.style.textDecoration = 'none')
                    }
                >
                    {content}
                </Link>
            );
        }

        if (
            ((childrenEnabled && row.children) || depth > 0) &&
            idx === (hasIdColumn ? 1 : 0)
        ) {
            const hasChildren = row.children && row.children.count > 0;
            wrappedContent = (
                <Box pl={depth * 2} display="flex" alignItems="center" gap={1}>
                    {depth > 0 && <SubdirectoryArrowRight />}
                    {content}
                    {hasChildren && (
                        <Chip
                            label={row.children.count}
                            size="small"
                            sx={{
                                height: 20,
                                minWidth: 20,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                backgroundColor: (theme) =>
                                    `${theme.palette.primary.main}26`,
                                color: 'var(--brand-text)',
                                '& .MuiChip-label': {
                                    px: 0.75,
                                },
                            }}
                        />
                    )}
                    {depth === 0 && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                if (hasChildren) {
                                    setVisibility(row, !row.childrenVisible);
                                }
                            }}
                            size="small"
                            disabled={!hasChildren}
                            sx={{ opacity: hasChildren ? 1 : 0.3 }}
                        >
                            {row.childrenVisible && hasChildren ? (
                                <ExpandMoreIcon />
                            ) : (
                                <ChevronRightIcon />
                            )}
                        </IconButton>
                    )}
                </Box>
            );
        }

        return (
            <TableCell
                key={idx}
                align={align}
                className={cls.join(' ')}
                style={col.style}
            >
                {wrappedContent}
            </TableCell>
        );
    };

    /**
     * Toggle selected row
     */
    const toggleSelectedRow = (row: any, _checked: boolean) => {
        const foundItem = selectedRows.find((e: any) => e.id === row.id);
        if (foundItem) {
            setSelectedRows(deleteElementFromArray(selectedRows, row) ?? []);
        } else setSelectedRows(updateElementInArray(selectedRows, row) ?? []);
    };

    /**
     * Get data of a specific page
     */
    const onPageChange = (page: number) => {
        if (localPagination) {
            return onPageChangeLocally(page);
        }

        if (loading) {
            setLoadingData(true);
        }
        setPage(page);

        if (onChangePagination) {
            onChangePagination(page + 1);
        }
        // hace falta +1 porque en back-end está hardcodeado que la primera página es la 1 en vez de la 0
        onFetch!({ page: page + 1, rows: rowsPerPage });
    };

    // Just set the page locally, no need to request new data
    const onPageChangeLocally = (page: number) => {
        setPage(page);
    };

    /**
     * Change rows per page
     */
    /*
    const handleChangeRowsPerPage = event => {
        const numRows = parseInt(event.target.value, 10);
        setRowsPerPage(numRows);
        setPage(1);
        onFetch({page:page, rows: numRows});
    }
    */

    function TablePaginationActions({
        count,
        page,
        rowsPerPage,
        onPageChange,
    }: {
        count: number;
        page: number;
        rowsPerPage: number;
        onPageChange: (event: any, newPage: number) => void;
    }) {
        const theme = useTheme();

        const handleFirstPageButtonClick = (event: any) => {
            onPageChange(event, 0);
        };

        const handleBackButtonClick = (event: any) => {
            onPageChange(event, page - 1);
        };

        const handleNextButtonClick = (event: any) => {
            onPageChange(event, page + 1);
        };

        const handleLastPageButtonClick = (event: any) => {
            onPageChange(
                event,
                Math.max(0, Math.ceil(count / rowsPerPage) - 1),
            );
        };

        return (
            <Box
                sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                }}
            >
                <IconButton
                    onClick={handleFirstPageButtonClick}
                    disabled={page === 0}
                    size="small"
                    sx={{
                        transition: 'all 0.2s ease',
                        '&:hover:not(:disabled)': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                        },
                    }}
                >
                    {theme.direction === 'rtl' ? (
                        <LastPageIcon />
                    ) : (
                        <FirstPageIcon />
                    )}
                </IconButton>
                <IconButton
                    onClick={handleBackButtonClick}
                    disabled={page === 0}
                    size="small"
                    sx={{
                        transition: 'all 0.2s ease',
                        '&:hover:not(:disabled)': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                        },
                    }}
                >
                    {theme.direction === 'rtl' ? (
                        <KeyboardArrowRight />
                    ) : (
                        <KeyboardArrowLeft />
                    )}
                </IconButton>
                <IconButton
                    onClick={handleNextButtonClick}
                    disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                    size="small"
                    sx={{
                        transition: 'all 0.2s ease',
                        '&:hover:not(:disabled)': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                        },
                    }}
                >
                    {theme.direction === 'rtl' ? (
                        <KeyboardArrowLeft />
                    ) : (
                        <KeyboardArrowRight />
                    )}
                </IconButton>
                <IconButton
                    onClick={handleLastPageButtonClick}
                    disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                    size="small"
                    sx={{
                        transition: 'all 0.2s ease',
                        '&:hover:not(:disabled)': {
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                        },
                    }}
                >
                    {theme.direction === 'rtl' ? (
                        <FirstPageIcon />
                    ) : (
                        <LastPageIcon />
                    )}
                </IconButton>
            </Box>
        );
    }

    TablePaginationActions.propTypes = {
        count: PropTypes.number.isRequired,
        onPageChange: PropTypes.func.isRequired,
        page: PropTypes.number.isRequired,
        rowsPerPage: PropTypes.number.isRequired,
    };

    /**
     * Snipped with pagination
     */
    const totalPages = internalData
        ? Math.ceil((internalData.count || 0) / rowsPerPage)
        : 0;

    const PaginationAdHoc = hidePagination ? null : (
        <TablePagination
            sx={{
                borderBottom: 0,
                '& .MuiTablePagination-toolbar': {
                    minHeight: 52,
                    paddingLeft: 2,
                    paddingRight: 1,
                },
                '& .MuiTablePagination-selectLabel': {
                    margin: 0,
                },
                '& .MuiTablePagination-displayedRows': {
                    margin: 0,
                    fontSize: '0.875rem',
                },
                '& .MuiTablePagination-actions': {
                    marginLeft: 1,
                },
            }}
            colSpan={100}
            labelDisplayedRows={() => {
                const count = internalData?.count || 0;
                return `${t('Page')} ${page + 1} ${t('of')} ${totalPages || 1} (${count} ${t('rows')})`;
            }}
            labelRowsPerPage={`${t('RowsPerPage')}:`}
            rowsPerPageOptions={[rowsPerPage]}
            count={internalData ? internalData.count || 0 : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
            }}
            onPageChange={(_ev, page) => onPageChange(page)}
            ActionsComponent={TablePaginationActions}
        />
    );

    // Rows which contain children will render them underneath, recursively
    function RecursiveTableRow({ row, depth }: { row: any; depth: number }) {
        const content = (
            <TableRow
                hover={Boolean(onClickRow || linkTo)}
                onClick={() => (onClickRow ? onClickRow(row) : null)}
                data-trash={row.trash || undefined}
                style={{
                    cursor: onClickRow || linkTo ? 'pointer' : 'default',
                }}
            >
                {selection && row.id && (
                    <TableCell>
                        <Checkbox
                            checked={
                                !!selectedRows.find((r: any) => r.id === row.id)
                            }
                            style={{ width: 42, height: 42 }}
                            onClick={(ev: any) => {
                                toggleSelectedRow(
                                    row,
                                    (ev.target as HTMLInputElement).checked,
                                );
                                ev.stopPropagation(); // stop event bubbling so the row's onClick callback isn't fired
                            }}
                        />
                    </TableCell>
                )}

                {columns?.map((col: any, index: any) => {
                    if (index === (hasIdColumn ? 1 : 0)) {
                        return renderCell(row, col, index, depth);
                    }
                    return renderCell(row, col, index);
                })}
            </TableRow>
        );
        return (
            <>
                {content}
                {childrenEnabled &&
                    row.children &&
                    row.childrenVisible &&
                    row.children.rows.map((child: any, index: number) => (
                        <RecursiveTableRow
                            key={index}
                            depth={depth + 1}
                            row={child}
                        />
                    ))}
            </>
        );
    }

    /**
     * Render totals at the end of the list
     */
    function renderTotalsRow() {
        // Do we have totals ???
        if (!totals || !data || data.rows.length === 0) {
            return null;
        }

        let usedColSpan = 0;

        return (
            <TableRow>
                {totals.map((item: any, i: any) => {
                    const { value, index, adornment } = item;

                    if (!value) {
                        return null;
                    }

                    const span = index + 1 - usedColSpan;
                    usedColSpan += index + 1;

                    return (
                        <TableCell key={i} colSpan={span}>
                            <Box
                                flex={1}
                                display="flex"
                                justifyContent="flex-end"
                            >
                                {value.toFixed(2) + (adornment || '')}
                            </Box>
                        </TableCell>
                    );
                })}
            </TableRow>
        );
    }

    let renderData = internalData;
    if (localPagination && internalData?.rows && internalData.rows.length > 0) {
        renderData = {
            count: rowsPerPage,
            rows: internalData.rows.slice(
                page * rowsPerPage,
                (page + 1) * rowsPerPage,
            ),
        };
    }

    // -----| Render |-----
    return (
        <>
            <Box
                display="flex"
                justifyContent="flex-end"
                borderBottom="1px solid rgba(224, 224, 224, 1)"
            >
                <Table>
                    <TableBody>
                        <TableRow>
                            {selectedRows &&
                                selectedRows.length > 0 &&
                                selection?.actions &&
                                selection.actions.length > 0 && (
                                    <TableCell>
                                        <SttMenu
                                            type="button"
                                            caption={`${t('Selected')}: ${
                                                selectedRows.length
                                            }`}
                                            icon=<CallSplitIcon />
                                            items={
                                                (selection?.actions &&
                                                    selection.actions.map(
                                                        (action: any) => ({
                                                            caption:
                                                                action.caption,
                                                            icon: action.icon,
                                                            onClick: () => {
                                                                action.onClick(
                                                                    selectedRows,
                                                                );
                                                                setSelectedRows(
                                                                    [],
                                                                );
                                                                setSelectedAllRows(
                                                                    false,
                                                                );
                                                            },
                                                        }),
                                                    )) as any
                                            }
                                        />
                                    </TableCell>
                                )}

                            {PaginationAdHoc}
                        </TableRow>
                        {loading && loadingData && (
                            <TableRow>
                                <TableCell colSpan={100} style={{ padding: 0 }}>
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>

            <TableContainer>
                <Table size="small">
                    {/* We only render the header if there is at least one column with a 'title' prop */}
                    {/* We check this at render time because otherwise there'll be a visible 'snap' if columns props is changed */}
                    {columns &&
                        columns.reduce(
                            (res, col) => !!col.title || res,
                            false,
                        ) && (
                            <TableHead>
                                <TableRow>
                                    {selection && (
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={selectedAllRows}
                                                style={{
                                                    width: 42,
                                                    height: 42,
                                                }}
                                                onClick={(ev: any) => {
                                                    setSelectedAllRows(
                                                        !selectedAllRows,
                                                    );
                                                    if (
                                                        (
                                                            ev.target as HTMLInputElement
                                                        ).checked
                                                    ) {
                                                        setSelectedRows(
                                                            renderData.rows,
                                                        );
                                                    } else {
                                                        setSelectedRows([]);
                                                    }
                                                    ev.stopPropagation(); // stop event bubbling so the row's onClick callback isn't fired
                                                }}
                                            />
                                        </TableCell>
                                    )}

                                    {columns &&
                                        columns.map((col, idxCol) =>
                                            renderHeader(col, idxCol),
                                        )}
                                </TableRow>
                            </TableHead>
                        )}

                    <TableBody>
                        {renderData?.rows?.map((row: any, idxRow: number) => (
                            <RecursiveTableRow
                                key={idxRow}
                                row={row}
                                depth={0}
                            />
                        ))}

                        {renderData && renderData.count === 0 && (
                            <TableRow>
                                <TableCell colSpan={100}>
                                    <SttNoResults />
                                </TableCell>
                            </TableRow>
                        )}

                        {renderTotalsRow()}

                        <TableRow>{PaginationAdHoc}</TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
}

// Comparison function to determine if a change in props really means a change in renderable data (see React.memo below)
function areEqual(prevProps: Props, nextProps: Props) {
    // First time render, probably
    if (!prevProps || !nextProps) {
        return false;
    }

    // Compare data count
    if (
        prevProps.data &&
        nextProps.data &&
        prevProps.data.count !== nextProps.data.count
    ) {
        return false;
    }

    // Compare loading variable
    if (prevProps.loading !== nextProps.loading) {
        return false;
    }

    if (prevProps.loadingValue !== nextProps.loadingValue) {
        return false;
    }

    // forceUpdateSwitch is used as a toggle to force redraws
    if (prevProps.forceUpdateSwitch !== nextProps.forceUpdateSwitch) {
        return false;
    }

    // Absent or different columns in any way
    if (
        (!prevProps.columns && !nextProps.columns) ||
        !!prevProps.columns !== !!nextProps.columns ||
        prevProps.columns?.length !== nextProps.columns?.length ||
        commaSeparated(
            prevProps.columns || [],
            (column) => column.title ?? '',
        ) !==
            commaSeparated(
                nextProps.columns || [],
                (column) => column.title ?? '',
            )
    ) {
        return false;
    }

    // No data or different types
    if (
        typeof prevProps.data !== typeof nextProps.data ||
        !prevProps.data ||
        !nextProps.data ||
        !prevProps.data.rows ||
        !nextProps.data.rows
    ) {
        return false;
    }

    // One of them empty or different # of rows
    if (
        (!prevProps.data.rows && nextProps.data.rows) ||
        (prevProps.data.rows && !nextProps.data.rows) ||
        prevProps.data.rows.length !== nextProps.data.rows.length
    ) {
        return false;
    }

    // Cheap check, compare all ids
    let sameIds = true;
    for (let i = 0; i < nextProps.data.rows.length; i++) {
        sameIds =
            sameIds && prevProps.data.rows[i].id === nextProps.data.rows[i].id;
    }

    if (!sameIds) {
        return false;
    }

    // Check if selectRows was updated.
    if (
        prevProps.selectRows &&
        nextProps.selectRows &&
        prevProps.selectRows.length !== nextProps.selectRows.length
    ) {
        return false;
    }

    // Final (and expensive) check, compare their JSON representation
    return (
        JSON.stringify(prevProps.data.rows) ===
        JSON.stringify(nextProps.data.rows)
    );
}

// React.memo is used to memoize the render result, using a comparison function to determine prop equality: https://reactjs.org/docs/react-api.html#reactmemo
export default React.memo(SttTable, areEqual);
